import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation, useParams, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import axios from 'axios';
import { 
  House, Package, ShoppingCart, Users, Gear, ChartLine,
  FileText, CreditCard, Truck, SignOut, List,
  Eye, Ticket, Globe, Cube, Palette, Megaphone,
  Image as ImageIcon, ArrowLeft, Bell, Storefront, Monitor, Sparkle, Handshake,
  CaretDown, CaretUp, Star, Tag, Chat, CurrencyEur,
  ArrowsClockwise, EnvelopeSimple, MapTrifold, User, IdentificationBadge, FolderOpen, MagnifyingGlass,
  ShieldCheck, ChatCircleDots
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_STORE_SLUG = "mrg";

const StoreAdminLayout = ({ storeSlug: propStoreSlug, standalone = false }) => {
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || DEFAULT_STORE_SLUG;
  const { user, logout } = useAuth();
  const { stores, selectStore, fetchStores, currentStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [storeIdentity, setStoreIdentity] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [openSections, setOpenSections] = useState({ catalogue: true, ventes: true, contenu: true, config: false });

  useEffect(() => { fetchStores(); }, [fetchStores]);

  useEffect(() => {
    if (stores.length > 0 && storeSlug) {
      const matchingStore = stores.find(s => s.slug === storeSlug);
      if (matchingStore) {
        if (!currentStore || currentStore.id !== matchingStore.id) selectStore(matchingStore);
      } else if (!standalone) {
        navigate('/dashboard');
      }
    }
  }, [stores, storeSlug, selectStore, currentStore, navigate, standalone]);

  useEffect(() => {
    if (!currentStore?.id) return;
    axios.get(`${API_URL}/api/stores/${currentStore.id}/identity`, { withCredentials: true })
      .then(res => setStoreIdentity(res.data)).catch(() => {});
    const loadStats = () => axios.get(`${API_URL}/api/stores/${currentStore.id}/stats?period=30d`, { withCredentials: true })
      .then(res => setQuickStats(res.data)).catch(() => {});
    loadStats();
    const it = setInterval(loadStats, 60000); // refresh every 60s
    return () => clearInterval(it);
  }, [currentStore?.id]);

  const handleLogout = async () => { await logout(); navigate(standalone ? '/admin/login' : '/login'); };

  const basePath = standalone ? '/admin' : `/store/${storeSlug}/admin`;
  const _rawPrimary = currentStore?.theme_config?.colors?.primary || storeIdentity?.primary_color || '#c2185b';
  const _rawAccent = currentStore?.theme_config?.colors?.accent || storeIdentity?.secondary_color || storeIdentity?.accent_color || '#F9055B';
  // Auto-fallback: if the primary color is too dark (near-black like #111), use accent for highlighting
  const _isDark = (hex) => {
    const m = (hex || '').replace('#', '');
    if (m.length < 6) return false;
    const [r, g, b] = [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
    return (r + g + b) / 3 < 40;
  };
  const primaryColor = _isDark(_rawPrimary) ? _rawAccent : _rawPrimary;
  const logoUrl = storeIdentity?.logo_light || storeIdentity?.logo_url || currentStore?.theme_config?.logo_url;
  const storeName = currentStore?.name || storeSlug;

  const sections = [
    {
      key: 'magasin',
      label: 'En magasin',
      items: [
        { icon: Monitor, label: 'Borne & RDV', path: `${basePath}/borne` },
        { icon: Sparkle, label: 'Assistant borne', path: `${basePath}/assistant` },
        { icon: IdentificationBadge, label: 'Équipe', path: `${basePath}/commerciaux` },
        { icon: Handshake, label: 'Partenaires', path: `${basePath}/partenaires` },
        { icon: ShieldCheck, label: 'Mutuelles', path: `${basePath}/mutuelles` },
        { icon: ChatCircleDots, label: 'Chat du site', path: `${basePath}/chat-settings` },
      ]
    },
    {
      key: 'enligne',
      label: 'En ligne',
      items: [
        { icon: ShoppingCart, label: 'Commandes', path: `${basePath}/orders`, badge: quickStats?.total_orders },
        { icon: Package, label: 'Produits', path: `${basePath}/products` },
        { icon: FolderOpen, label: 'Catégories', path: `${basePath}/categories` },
        { icon: Cube, label: 'Stock', path: `${basePath}/stock` },
        { icon: ImageIcon, label: 'Médias', path: `${basePath}/media` },
        { icon: Users, label: 'Clients', path: `${basePath}/customers` },
        { icon: MapTrifold, label: 'Carte clients', path: `${basePath}/customers-map` },
        { icon: CreditCard, label: 'Historique paiements', path: `${basePath}/payments` },
        { icon: Truck, label: 'Expédition', path: `${basePath}/shipping` },
        { icon: Truck, label: 'Livreurs', path: `${basePath}/drivers` },
        { icon: ChartLine, label: 'Performances', path: `${basePath}/stats` },
        { icon: MagnifyingGlass, label: 'Suivi SEO Google', path: `${basePath}/seo-rank` },
        { icon: FileText, label: 'Blog', path: `${basePath}/blog` },
        { icon: Ticket, label: 'Marketing', path: `${basePath}/marketing` },
        { icon: Megaphone, label: 'Popups', path: `${basePath}/popups` },
        { icon: Star, label: 'Fidélité', path: `${basePath}/loyalty` },
      ]
    },
    {
      key: 'config',
      label: 'Configuration',
      items: [
        { icon: Palette, label: 'Identité visuelle', path: `${basePath}/identity` },
        { icon: CreditCard, label: 'Méthodes de paiement', path: `${basePath}/payment-methods` },
        { icon: EnvelopeSimple, label: 'Notifications email', path: `${basePath}/notifications` },
        { icon: User, label: 'Mon compte', path: `${basePath}/account` },
        { icon: Gear, label: 'Paramètres', path: `${basePath}/settings` },
      ]
    },
  ];

  const isActive = (path) => location.pathname === path;
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#f8f7f5' }} data-testid="store-admin-layout">
      {/* ═══════ SIDEBAR ═══════ */}
      <aside 
        className={`${sidebarOpen ? 'w-[260px]' : 'w-[72px]'} transition-all duration-300 flex flex-col border-r border-slate-200/60 fixed left-0 top-0 bottom-0 z-50`}
        style={{ backgroundColor: '#111111' }}
      >
        {/* Store branding */}
        <div className="px-4 py-5 border-b border-white/8">
          <Link to={basePath} className="flex items-center gap-3" data-testid="store-admin-logo">
            {logoUrl ? (
              <img src={logoUrl} alt={storeName} className="h-11 w-11 object-contain rounded-xl bg-white/10 p-0.5" />
            ) : (
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                <span className="text-white font-black text-base">{storeName.charAt(0)}</span>
              </div>
            )}
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-[15px] truncate leading-tight">{storeName}</p>
                <p className="text-[11px] font-medium mt-0.5 truncate" style={{ color: primaryColor }}>Back-office</p>
              </div>
            )}
          </Link>
        </div>

        {/* Quick actions */}
        {sidebarOpen && (
          <div className="px-3 py-3 border-b border-white/8 flex gap-2">
            <Button 
              variant="ghost" size="sm"
              className="flex-1 text-[11px] h-8 text-white/60 hover:text-white hover:bg-white/8 rounded-lg"
              onClick={() => window.open(standalone ? '/' : `/store/${storeSlug}`, '_blank')}
              data-testid="preview-store-btn"
            >
              <Eye size={13} className="mr-1" /> Voir le site
            </Button>
            {!standalone && (
              <Button 
                variant="ghost" size="sm"
                className="flex-1 text-[11px] h-8 text-white/60 hover:text-white hover:bg-white/8 rounded-lg"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-admin-btn"
              >
                <ArrowLeft size={13} className="mr-1" /> Admin
              </Button>
            )}
          </div>
        )}

        {/* Dashboard link */}
        <div className="px-3 pt-3 pb-1">
          <Link
            to={basePath}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
              isActive(basePath) ? 'text-white shadow-lg' : 'text-white/55 hover:text-white hover:bg-white/5'
            }`}
            style={isActive(basePath) ? { backgroundColor: primaryColor, boxShadow: `0 4px 12px ${primaryColor}40` } : {}}
            data-testid="store-nav-dashboard"
          >
            <House size={20} weight={isActive(basePath) ? 'fill' : 'regular'} />
            {sidebarOpen && <span className="text-[13px] font-semibold">Tableau de bord</span>}
          </Link>
        </div>

        {/* Navigation sections */}
        <nav className="flex-1 py-2 overflow-y-auto px-3">
          {sections.map(section => (
            <div key={section.key} className="mb-1">
              {sidebarOpen && (
                <button
                  onClick={() => toggleSection(section.key)}
                  className="flex items-center justify-between w-full px-3 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-white/30 hover:text-white/50 transition-colors"
                >
                  {section.label}
                  {openSections[section.key] ? <CaretUp size={12} /> : <CaretDown size={12} />}
                </button>
              )}
              {(openSections[section.key] || !sidebarOpen) && (
                <ul className="space-y-0.5">
                  {section.items.map(item => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 group ${
                          isActive(item.path)
                            ? 'text-white'
                            : 'text-white/50 hover:text-white hover:bg-white/5'
                        }`}
                        style={isActive(item.path) ? { backgroundColor: primaryColor + '20', color: primaryColor } : {}}
                        data-testid={`store-nav-${item.label.toLowerCase().replace(/[\s&]/g, '-')}`}
                      >
                        <item.icon size={18} weight={isActive(item.path) ? 'fill' : 'regular'} />
                        {sidebarOpen && (
                          <span className="text-[13px] font-medium flex-1">{item.label}</span>
                        )}
                        {item.badge > 0 && sidebarOpen && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: primaryColor }}>
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </nav>

        {/* User section */}
        <div className="p-3 border-t border-white/8">
          <div className="flex items-center gap-3 px-2">
            <Link
              to={`${basePath}/account`}
              className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm overflow-hidden hover:ring-2 hover:ring-white/30 transition-all"
              style={{ backgroundColor: primaryColor }}
              data-testid="admin-account-link"
              title="Mon compte"
            >
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url.startsWith('/') ? `${API_URL}${user.avatar_url}` : user.avatar_url}
                  alt={user?.name || ''}
                  className="w-full h-full object-cover"
                />
              ) : (
                user?.name?.charAt(0).toUpperCase() || 'U'
              )}
            </Link>
            {sidebarOpen && (
              <Link to={`${basePath}/account`} className="flex-1 min-w-0 hover:opacity-80 transition-opacity" data-testid="admin-account-name-link">
                <p className="text-[13px] font-medium text-white truncate">{user?.name}</p>
                <p className="text-[11px] text-white/40 truncate">{user?.role === 'admin' ? 'Administrateur' : 'Marchand'}</p>
              </Link>
            )}
            <button onClick={handleLogout} className="text-white/40 hover:text-white transition-colors" data-testid="store-admin-logout-btn">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* ═══════ MAIN CONTENT ═══════ */}
      <div className={`flex-1 flex flex-col ${sidebarOpen ? 'ml-[260px]' : 'ml-[72px]'} transition-all duration-300`}>
        {/* Top header bar */}
        <header className="h-14 bg-white border-b border-slate-200/60 sticky top-0 z-40 flex items-center px-6 gap-4">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="toggle-sidebar"
          >
            <List size={20} />
          </button>
          
          <div className="flex-1" />
          
          {/* Quick stats in header */}
          {quickStats && (
            <div className="hidden lg:flex items-center gap-5 text-sm" data-testid="admin-header-stats">
              <div className="flex items-center gap-1.5" title="Chiffre d'affaires du jour">
                <CurrencyEur size={15} className="text-emerald-500" />
                <span className="text-slate-500">CA jour :</span>
                <span className="font-bold" style={{ color: primaryColor }} data-testid="stat-ca-today">
                  {(quickStats.revenue_today || 0).toFixed(0)} €
                </span>
                {quickStats.orders_today > 0 && (
                  <span className="text-[10px] text-slate-400">({quickStats.orders_today})</span>
                )}
              </div>
              <span className="text-slate-200">|</span>
              <div className="flex items-center gap-1.5" title="Chiffre d'affaires du mois en cours">
                <ChartLine size={15} className="text-blue-500" />
                <span className="text-slate-500">CA mois :</span>
                <span className="font-bold" style={{ color: primaryColor }} data-testid="stat-ca-month">
                  {(quickStats.revenue_month || 0).toFixed(0)} €
                </span>
                {quickStats.orders_month > 0 && (
                  <span className="text-[10px] text-slate-400">({quickStats.orders_month})</span>
                )}
              </div>
              <span className="text-slate-200">|</span>
              <div className="flex items-center gap-1.5" title="Panier moyen du mois">
                <ShoppingCart size={15} className="text-amber-500" />
                <span className="text-slate-500">Panier :</span>
                <span className="font-bold" data-testid="stat-avg-basket">
                  {(quickStats.avg_basket_month || quickStats.avg_basket || 0).toFixed(0)} €
                </span>
              </div>
              <span className="text-slate-200">|</span>
              <div className="flex items-center gap-1.5" title="Nombre total de clients">
                <Users size={15} className="text-violet-500" />
                <span className="text-slate-500">Clients :</span>
                <span className="font-bold" data-testid="stat-customers">
                  {quickStats.customers_count || 0}
                </span>
              </div>
              <span className="text-slate-200">|</span>
              <div className="flex items-center gap-1.5" title="Nombre de produits en catalogue">
                <Package size={15} className="text-slate-400" />
                <span className="text-slate-500">Produits :</span>
                <span className="font-bold" data-testid="stat-products">
                  {quickStats.products_count || 0}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 ml-4">
            <Button 
              variant="outline" size="sm" 
              className="text-xs h-8 gap-1.5"
              onClick={() => window.open(standalone ? '/' : `/store/${storeSlug}`, '_blank')}
            >
              <Storefront size={14} />
              Voir le site
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default StoreAdminLayout;
